import { withTranslation, WithTranslation } from 'react-i18next';
import React, { ChangeEvent, useEffect, useState } from 'react';
import getManagingSoftwares from '../../../../helpers/managingSoftwares';
import i18n from 'i18next';
import { handleInputWithLabelOnBlur, handleInputWithLabelOnFocus } from '../../registration';
import InputField from '../../../form/inputField';
import DropdownField from '../../../form/dropdownField';
import PcaField from '../../../form/pcaField';
import PopoverContainer from '../../../popover/popover';
import { ISoftwareModel } from '../../../../helpers/models/softwareModel';
import { OrganizationStep1FormResultKeys } from './organizationStep1FormModel';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import {
    FORM_PHONE_EXT_MAX_LENGTH,
    FORM_PHONE_MAX_LENGTH,
    validatePhoneNumberFormat,
} from '../../utils/formValidation';

export interface IOrganizationStep1OrganizationProfileProps extends WithTranslation {
    account: IAccountModel;
    readonly?: boolean;
    updateStep?: any;
}

const OrganizationStep1OrganizationProfile = (
    props: IOrganizationStep1OrganizationProfileProps
) => {
    const { t, readonly, account, updateStep } = props;
    const [enabledSoftwares, setEnabledSoftwares] = useState<JSX.Element[]>([]);
    const [disabledSoftwares, setDisabledSoftwares] = useState<JSX.Element[]>([]);
    const [otherSoftwares, setOtherSoftwares] = useState<JSX.Element[]>([]);

    const ENABLED_TYPE = 'enabled';
    const DISABLED_TYPE = 'notenabled';

    useEffect(() => {
        async function getData() {
            const softwaresList = await getManagingSoftwares(i18n.language);
            if (softwaresList) {
                setEnabledSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type === ENABLED_TYPE;
                        })
                        .map((software: ISoftwareModel) => {
                            return (
                                <option
                                    key={software.id}
                                    value={software.id}
                                    data-datatype={software.type}
                                >
                                    {software.name}
                                </option>
                            );
                        })
                );
                setDisabledSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type === DISABLED_TYPE;
                        })
                        .map((software: ISoftwareModel) => {
                            return (
                                <option
                                    key={software.id}
                                    value={software.id}
                                    data-datatype={software.type}
                                >
                                    {software.name}
                                </option>
                            );
                        })
                );

                setOtherSoftwares(
                    softwaresList!
                        .filter((item: ISoftwareModel) => {
                            return item.type !== DISABLED_TYPE && item.type !== ENABLED_TYPE;
                        })
                        .map((software: ISoftwareModel) => {
                            return (
                                <option key={software.id} value={software.id}>
                                    {software.name}
                                </option>
                            );
                        })
                );
            }
        }
        getData();
    }, [i18n.language]);

    const softwares = [
        <React.Fragment key="software">
            {enabledSoftwares.length ? (
                <optgroup
                    key="enabled"
                    label={t('form_organization_step_1_field_managing_software_activated')}
                >
                    {enabledSoftwares}
                </optgroup>
            ) : null}
            {disabledSoftwares.length ? (
                <optgroup
                    key="notenabled"
                    label={t('form_organization_step_1_field_managing_software_disabled')}
                >
                    {disabledSoftwares}
                </optgroup>
            ) : null}
            {otherSoftwares}
        </React.Fragment>,
    ];

    const operations: any[] = [];
    const currentYear = new Date().getFullYear() as number;
    for (let i = currentYear; i >= 1900; --i) {
        operations.push(
            <option key={i} value={i}>
                {i.toString()}
            </option>
        );
    }

    const handleSoftwareChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const isEnabled = e.target[e.target.selectedIndex].getAttribute('datatype') === 'enabled';
        document.getElementById('form_organization_eligible_label')!.style.display = isEnabled
            ? 'unset'
            : 'none';

        if (!isEnabled) {
            (
                document.getElementById(
                    OrganizationStep1FormResultKeys.COMMUNICATION
                )! as HTMLInputElement
            ).checked = false;
        }

        const softwareOtherElement = document.getElementById(
            'form_organization_step_1_software_other'
        )! as HTMLInputElement;
        softwareOtherElement.style.display = e.target.value === '1' ? 'unset' : 'none';
        if (e.target.value === '1') {
            softwareOtherElement.setAttribute('required', '');
        } else {
            softwareOtherElement.removeAttribute('required');
            (
                document.getElementById(
                    OrganizationStep1FormResultKeys.MANAGING_SOFTWARE_OTHER
                )! as HTMLInputElement
            ).value = '';
        }
    };

    useEffect(() => {
        if (account.organization?.managementSoftware) {
            const softwareSelectElement = document.getElementById(
                OrganizationStep1FormResultKeys.MANAGING_SOFTWARE
            )! as HTMLSelectElement;
            if (softwareSelectElement?.options.length > 1) {
                document.getElementById('form_organization_eligible_label')!.style.display =
                    softwareSelectElement.options[softwareSelectElement.selectedIndex].getAttribute(
                        'data-datatype'
                    ) === 'enabled'
                        ? 'unset'
                        : 'none';
                document.getElementById('form_organization_step_1_software_other')!.style.display =
                    softwareSelectElement.options[softwareSelectElement.options.selectedIndex]
                        ?.value === '1'
                        ? 'unset'
                        : 'none';
            }
        }
    }, [softwares]);

    return (
        <>
            <div className="registration_form_header_section registration-form-revise-submit--modify-section">
                <h1 className="header-3 header-3--purple registration_form_header">
                    {t('form_organization_step_1_profile_title')}
                </h1>
                {readonly && (
                    <a
                        data-step={1}
                        className="standalone-link registration_form--modify-button"
                        tabIndex={0}
                        role="button"
                        onClick={updateStep}
                        aria-label={t('form_organization_step_1_profile_title')
                            .addSpace()
                            .concat(t('form_revise_submit_organization_step_1_modify_button'))}
                    >
                        {t('form_revise_submit_organization_step_1_modify_button')}
                        <span className="registration-from--modify-button-icon"></span>
                    </a>
                )}
            </div>
            <div className="registration-form-container">
                <div className="registration-section">
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.COMMERCIAL_NAME}
                        fieldTitle="form_organization_step_1_field_commercial_name"
                        fieldDisabled={readonly}
                        fieldRequired
                        type="text"
                        defaultValue={account.organization?.name ?? ''}
                        fieldWeight={0}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.LEGAL_NAME}
                        fieldTitle="form_organization_step_1_field_legal_name"
                        fieldDisabled={readonly}
                        fieldRequired
                        type="text"
                        defaultValue={account.organization?.legalName ?? ''}
                        fieldWeight={1}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />

                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item"
                            fieldName={OrganizationStep1FormResultKeys.ORGANIZATION_TYPE}
                            fieldTitle="form_organization_step_1_field_organization_type"
                            fieldDisabled
                            type="text"
                            value={(account.role as IRoleModel).name}
                            fieldWeight={0}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                        />
                        <DropdownField
                            fieldContainerClassName="registration-field registration-column-item"
                            fieldInputClass="field-date"
                            fieldName={OrganizationStep1FormResultKeys.ORGANIZATION_OPERATION}
                            fieldTitle="form_organization_step_1_field_operation"
                            fieldDefaultValue="form_organization_step_1_field_operation_default"
                            fieldOptions={operations}
                            fieldDisabled={readonly}
                            fieldRequired
                            value={account.organization?.operatingYear ?? ''}
                            fieldWeight={2}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                        />
                    </div>
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.EMAIL}
                        fieldTitle="form_organization_step_1_field_email"
                        fieldTooltip={
                            <PopoverContainer
                                icon
                                className={'field-tooltip-icon'}
                                prefix={t('form_organization_step_1_field_email')}
                            >
                                <p className="body-medium">
                                    {t('form_organization_step_1_field_email_popover')}
                                </p>
                            </PopoverContainer>
                        }
                        fieldDisabled={readonly}
                        fieldRequired
                        type="email"
                        defaultValue={account.organization?.email ?? ''}
                        fieldWeight={3}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.CSPAAT}
                        fieldTitle="form_organization_step_1_field_id_cspaat"
                        fieldOptionalText="form_organization_step_1_field_id_cspaat_optional"
                        fieldDisabled={readonly}
                        type="text"
                        defaultValue={account.organization?.number ?? ''}
                        fieldWeight={4}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.COMMERCIAL_BANNER}
                        fieldTitle="form_organization_step_1_field_commercial_banner"
                        fieldOptionalText="form_organization_step_1_field_commercial_banner_optional"
                        fieldTooltip={
                            <PopoverContainer
                                icon
                                className={'field-tooltip-icon'}
                                prefix={t('form_organization_step_1_field_commercial_banner')}
                            >
                                <p className="body-medium">
                                    {t('form_organization_step_1_field_commercial_banner_popover')}
                                </p>
                            </PopoverContainer>
                        }
                        fieldDisabled={readonly}
                        fieldDisabledPopover={readonly}
                        type="text"
                        defaultValue={account.organization?.retailBanner ?? ''}
                        fieldWeight={5}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                </div>
                <div className="registration-section">
                    {!readonly && (
                        <PcaField
                            fieldContainerClassName="registration-field"
                            fieldName={OrganizationStep1FormResultKeys.PCA}
                            fieldTitle="form_organization_step_1_field_pca"
                            fieldId="form_organization_step_1_field_pca"
                            fieldPca="Line1"
                            fieldDisabled={readonly}
                            defaultValue={account.organization?.address ?? ''}
                            fieldAddress={OrganizationStep1FormResultKeys.ADDRESS}
                            fieldCity={OrganizationStep1FormResultKeys.CITY}
                            fieldPostalCode={OrganizationStep1FormResultKeys.POSTAL_CODE}
                            fieldWeight={5}
                            fieldAccountProvinceCode={(account.province as IProvinceModel).pca}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                            fieldDisabledPopover={readonly}
                            fieldTooltip={
                                <PopoverContainer
                                    icon
                                    className={'field-tooltip-icon'}
                                    prefix={t('form_organization_step_1_pca_address')}
                                >
                                    <p className="body-medium">
                                        {t('form_organization_step_1_field_pca_popover')}
                                    </p>
                                </PopoverContainer>
                            }
                        />
                    )}
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.ADDRESS}
                        fieldTitle="form_organization_step_1_field_address"
                        fieldDisabled
                        type="text"
                        defaultValue={account.organization?.address ?? ''}
                        fieldWeight={6}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                    <InputField
                        fieldContainerClassName="registration-field"
                        fieldName={OrganizationStep1FormResultKeys.CITY}
                        fieldTitle="form_organization_step_1_field_city"
                        fieldDisabled
                        type="text"
                        defaultValue={account.organization?.city ?? ''}
                        fieldWeight={6}
                        fieldHiddenLabel="form_organization_step_1_profile_title"
                    />
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item"
                            fieldName={OrganizationStep1FormResultKeys.PROVINCE}
                            fieldTitle="form_organization_step_1_field_province"
                            fieldDisabled
                            type="text"
                            value={(account.province as IProvinceModel)?.name ?? ''}
                            fieldWeight={7}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                            fieldTooltip={
                                <PopoverContainer
                                    icon
                                    prefix={t('form_organization_step_1_field_province')}
                                    className={'field-tooltip-icon'}
                                    triggerContent={t('form_organization_step_1_field_province_inline')}
                                >
                                    <>
                                        <p className="body-small">
                                            <strong>
                                                {t('form_organization_step_1_field_province_inline')}
                                            </strong>
                                        </p>
                                        <p className="body-medium">
                                            {t(
                                                'form_organization_step_1_field_province_popover_register_locations'
                                            )}
                                        </p>
                                        <p className="body-medium">
                                            {t('form_organization_step_1_field_province_popover_tel')}
                                            <br/>
                                            <a
                                                className="standalone-link"
                                                tabIndex={-1}
                                                href={`mailto:${t(
                                                    'form_organization_step_1_field_province_popover_email'
                                                )}`}
                                            >
                                                {t(
                                                    'form_organization_step_1_field_province_popover_email'
                                                )}
                                            </a>
                                        </p>
                                    </>
                                </PopoverContainer>
                            }
                        />
                        <InputField
                            fieldContainerClassName="registration-field"
                            fieldName={OrganizationStep1FormResultKeys.POSTAL_CODE}
                            fieldTitle="form_organization_step_1_field_postal_code"
                            fieldDisabled
                            type="text"
                            defaultValue={account.organization?.zip ?? ''}
                            fieldWeight={8}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                        />
                    </div>
                    <div className="registration-column-container">
                        <InputField
                            fieldContainerClassName="registration-field registration-contact-info-phone-field registration-column-item"
                            fieldName={OrganizationStep1FormResultKeys.PHONE}
                            fieldTitle="form_organization_step_1_field_phone"
                            fieldDisabled={readonly}
                            fieldRequired
                            inputMode="numeric"
                            placeholder="999-999-9999"
                            defaultValue={account.organization?.phone ?? ''}
                            maxLength={FORM_PHONE_MAX_LENGTH}
                            handleOnChange={validatePhoneNumberFormat}
                            fieldWeight={9}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                        />
                        <InputField
                            fieldContainerClassName="registration-field registration-column-item registration-contact-info-ext-field"
                            fieldName={OrganizationStep1FormResultKeys.PHONE_EXT}
                            fieldTitle="form_organization_step_1_field_phone_ext"
                            fieldOptionalText="form_organization_step_1_field_phone_ext_optional"
                            fieldDisabled={readonly}
                            inputMode="numeric"
                            maxLength={FORM_PHONE_EXT_MAX_LENGTH}
                            defaultValue={account.organization?.ext ?? ''}
                            fieldWeight={10}
                            handleOnChange={validatePhoneNumberFormat}
                            fieldHiddenLabel="form_organization_step_1_profile_title"
                        />
                    </div>
                </div>
            </div>
            <div className="registration-column-container">
                <DropdownField
                    fieldContainerClassName="registration-field registration-column-item registration-field--no-margin"
                    fieldName={OrganizationStep1FormResultKeys.MANAGING_SOFTWARE}
                    fieldTitle="form_organization_step_1_field_managing_software"
                    fieldDisabled={readonly}
                    fieldDisabledPopover={readonly}
                    fieldWeight={11}
                    fieldHiddenLabel="form_organization_step_1_profile_title"
                    fieldTooltip={
                        <PopoverContainer
                            icon
                            className={'field-tooltip-icon'}
                            prefix={t('form_organization_step_1_field_managing_software')}
                        >
                            <>
                                <p className="body-medium">
                                    {t(
                                        'form_organization_step_1_field_managing_software_popover_authorisation'
                                    )}
                                </p>
                                <ul>
                                    <li className="body-medium">
                                        {t(
                                            'form_organization_step_1_field_managing_software_popover_info_share'
                                        )}
                                    </li>
                                    <li className="body-medium">
                                        {t(
                                            'form_organization_step_1_field_managing_software_popover_facilitate_service'
                                        )}
                                    </li>
                                </ul>
                                <p className="body-medium">
                                    <i>
                                        {t(
                                            'form_organization_step_1_field_managing_software_popover_subscription_fee'
                                        )}
                                    </i>
                                </p>
                            </>
                        </PopoverContainer>
                    }
                    fieldDefaultValue="form_organization_step_1_field_managing_software_default"
                    fieldOptions={softwares}
                    fieldRequired
                    value={(account.organization?.managementSoftware as ISoftwareModel)?.id ?? ''}
                    handleOnChange={handleSoftwareChange}
                />
                <InputField
                    fieldContainerClassName="registration-field registration-field--no-margin-lg registration-column-item"
                    fieldName={OrganizationStep1FormResultKeys.MANAGING_SOFTWARE_OTHER}
                    fieldTitle="form_organization_step_1_field_managing_software_other"
                    fieldId="form_organization_step_1_software_other"
                    fieldDisabled={readonly}
                    defaultValue={account.organization?.managementSoftwareOther ?? ''}
                    type="text"
                    fieldWeight={11}
                    fieldHiddenLabel="form_organization_step_1_profile_title"
                />
            </div>
            <label
                id="form_organization_eligible_label"
                htmlFor={OrganizationStep1FormResultKeys.COMMUNICATION}
                className={'checkbox form_organization_eligible_label '.concat(
                    readonly ? 'field-label--disabled' : ''
                )}
            >
                <div>{t('form_organization_step_1_field_communication')}</div>
                <input
                    className="checkbox-input field-value"
                    type="checkbox"
                    id={OrganizationStep1FormResultKeys.COMMUNICATION}
                    name={OrganizationStep1FormResultKeys.COMMUNICATION}
                    onBlur={handleInputWithLabelOnBlur}
                    onFocus={handleInputWithLabelOnFocus}
                    disabled={readonly}
                    defaultChecked={account.organization?.agreeManagementSoftware}
                />
                <span className="checkmark checkmark--disabled"></span>
            </label>
        </>
    );
};

export default withTranslation()(OrganizationStep1OrganizationProfile);
